import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { type ReactNode } from 'react';
import { PostHogProvider } from '@posthog/react';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: (failureCount, error) => {
        if (error instanceof Error && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
            return false;
          }
        }
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
    },
  },
});

// Rails injects the token as a <meta> tag at request time (see
// PosthogMetaTaggable) so a single Docker image is environment-correct
// everywhere — the key isn't baked in at build time. import.meta.env is
// kept as a fallback for local dev only (see VITE_POSTHOG_PROJECT_TOKEN /
// VITE_PUBLIC_POSTHOG_PROJECT_TOKEN in .envrc).
function metaContent(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  return document.querySelector(`meta[name="${name}"]`)?.getAttribute('content') ?? undefined;
}

const posthogKey =
  metaContent('posthog-key') ||
  import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN ||
  import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;

const posthogHost =
  metaContent('posthog-host') || import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

const posthogEnvironment = metaContent('posthog-env') || import.meta.env.MODE;

interface ProvidersProps {
  children: ReactNode;
}

export function Providers({ children }: ProvidersProps) {
  const shouldShowDevtools =
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    window.localStorage.getItem('showReactQueryDevtools') === 'true';

  const content = (
    <QueryClientProvider client={queryClient}>
      {children}
      {shouldShowDevtools ? (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      ) : null}
    </QueryClientProvider>
  );

  // No key (production today, until GRAIN-2467 provisions one) → render
  // children with no PostHog provider at all. posthog-js is never
  // init()'d, so nothing captures and nothing crashes.
  if (!posthogKey) {
    return content;
  }

  return (
    <PostHogProvider
      apiKey={posthogKey}
      options={{
        api_host: posthogHost,
        defaults: '2026-01-30',
        capture_exceptions: true,
        debug: import.meta.env.DEV,
        loaded: (client) => {
          client.register({ environment: posthogEnvironment });
        },
      }}
    >
      {content}
    </PostHogProvider>
  );
}

export { queryClient };
